import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Bell, Mail, MessageSquare, Info } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface EventTypeWithPreference {
  id: string;
  name: string;
  description: string | null;
  category: string;
  preference?: {
    id: string;
    in_app_enabled: boolean;
    email_enabled: boolean;
    sms_enabled: boolean;
    digest_enabled: boolean;
    digest_frequency: string;
  };
}

export const NotificationPreferences = () => {
  const [eventTypes, setEventTypes] = useState<EventTypeWithPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [appUserId, setAppUserId] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      fetchPreferences();
    }
  }, [user]);

  const fetchPreferences = async () => {
    try {
      // Get app_user id
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (!appUser) return;
      setAppUserId(appUser.id);

      // Fetch event types
      const { data: types } = await supabase
        .from('notification_event_types')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true });

      // Fetch user preferences
      const { data: prefs } = await supabase
        .from('user_notification_preferences')
        .select('*')
        .eq('user_id', appUser.id);

      // Merge preferences with event types
      const mergedData = (types || []).map(type => ({
        ...type,
        preference: prefs?.find(p => p.event_type_id === type.id)
      }));

      setEventTypes(mergedData);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (
    eventTypeId: string,
    field: string,
    value: boolean | string
  ) => {
    if (!appUserId) return;

    try {
      const existingPref = eventTypes.find(e => e.id === eventTypeId)?.preference;

      if (existingPref) {
        // Update existing
        const { error } = await supabase
          .from('user_notification_preferences')
          .update({ [field]: value, updated_at: new Date().toISOString() })
          .eq('id', existingPref.id);

        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from('user_notification_preferences')
          .insert({
            user_id: appUserId,
            event_type_id: eventTypeId,
            [field]: value
          });

        if (error) throw error;
      }

      // Update local state
      setEventTypes(prev => prev.map(et => {
        if (et.id === eventTypeId) {
          return {
            ...et,
            preference: {
              id: existingPref?.id || '',
              in_app_enabled: existingPref?.in_app_enabled ?? true,
              email_enabled: existingPref?.email_enabled ?? true,
              sms_enabled: existingPref?.sms_enabled ?? true,
              digest_enabled: existingPref?.digest_enabled ?? false,
              digest_frequency: existingPref?.digest_frequency ?? 'daily',
              [field]: value
            }
          };
        }
        return et;
      }));

      toast({ title: 'Preference updated' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'candidate': return 'Candidate Events';
      case 'job': return 'Job Events';
      case 'communication': return 'Communication Events';
      default: return 'General Events';
    }
  };

  const formatEventName = (name: string) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Group by category
  const grouped = eventTypes.reduce((acc, et) => {
    if (!acc[et.category]) acc[et.category] = [];
    acc[et.category].push(et);
    return acc;
  }, {} as Record<string, EventTypeWithPreference[]>);

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {Object.entries(grouped).map(([category, types]) => (
          <Card key={category} className="p-6">
            <h3 className="text-lg font-semibold mb-4">{getCategoryLabel(category)}</h3>
            <div className="space-y-4">
              {types.map(eventType => (
                <div key={eventType.id} className="flex items-center justify-between py-3 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{formatEventName(eventType.name)}</span>
                        {eventType.description && (
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="h-4 w-4 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{eventType.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Bell className="h-4 w-4 text-muted-foreground" />
                      <Switch
                        checked={eventType.preference?.in_app_enabled ?? true}
                        onCheckedChange={(checked) => updatePreference(eventType.id, 'in_app_enabled', checked)}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <Switch
                        checked={eventType.preference?.email_enabled ?? true}
                        onCheckedChange={(checked) => updatePreference(eventType.id, 'email_enabled', checked)}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <Switch
                        checked={eventType.preference?.sms_enabled ?? true}
                        onCheckedChange={(checked) => updatePreference(eventType.id, 'sms_enabled', checked)}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">Digest</Badge>
                      <Switch
                        checked={eventType.preference?.digest_enabled ?? false}
                        onCheckedChange={(checked) => updatePreference(eventType.id, 'digest_enabled', checked)}
                      />
                      {eventType.preference?.digest_enabled && (
                        <Select
                          value={eventType.preference?.digest_frequency ?? 'daily'}
                          onValueChange={(value) => updatePreference(eventType.id, 'digest_frequency', value)}
                        >
                          <SelectTrigger className="w-24 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </TooltipProvider>
  );
};
